import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React from "react";
import { CampaignGroup } from "../types/lead-contacts.types";

interface LeadContactsControlsProps {
  groupedContacts: Record<string, CampaignGroup>;
  expandedCampaigns: Set<string>;
  selectedLeads: Set<number>;
  copiedLeadIds: Set<number>;
  showCopiedLeads: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onClearSelection: () => void;
  onToggleCopiedLeads: () => void;
}

const LeadContactsControls: React.FC<LeadContactsControlsProps> = ({
  groupedContacts,
  expandedCampaigns,
  selectedLeads,
  copiedLeadIds,
  showCopiedLeads,
  onExpandAll,
  onCollapseAll,
  onClearSelection,
  onToggleCopiedLeads,
}) => {
  const allCampaigns = Object.keys(groupedContacts);
  const allExpanded =
    allCampaigns.length > 0 &&
    allCampaigns.every((campaign) => expandedCampaigns.has(campaign));

  return (
    <Box
      sx={{
        // Removido padding, border e background pois agora está no ControlsContainer
        width: "100%",
      }}
    >
      {/* Controles compactos em uma linha */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {/* Lado esquerdo: Controle de campanhas */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Tooltip
            title={
              allExpanded
                ? "Recolher todas as campanhas"
                : "Expandir todas as campanhas"
            }
            arrow
          >
            <Box
              component="button"
              onClick={allExpanded ? onCollapseAll : onExpandAll}
              sx={{
                backgroundColor: allExpanded ? "transparent" : "#008069",
                color: allExpanded ? "#008069" : "white",
                border: "1px solid #008069",
                borderRadius: "16px",
                minHeight: "26px",
                padding: "2px 10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "11px",
                minWidth: "auto",
                boxShadow: allExpanded
                  ? "none"
                  : "0 1px 3px rgba(0, 128, 105, 0.2)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                outline: "none",
                "&:focus-visible": {
                  outline: "2px solid #008069",
                  outlineOffset: "2px",
                  boxShadow: "0 0 0 2px rgba(0, 128, 105, 0.2)",
                },
                "&:hover": {
                  backgroundColor: allExpanded
                    ? "rgba(0, 128, 105, 0.1)"
                    : "#006d5b",
                  borderColor: "#006d5b",
                  transform: "translateY(-1px)",
                  boxShadow: allExpanded
                    ? "0 2px 6px rgba(0, 128, 105, 0.15)"
                    : "0 2px 6px rgba(0, 128, 105, 0.3)",
                },
              }}
            >
              {allExpanded ? (
                <ExpandLessIcon sx={{ fontSize: "14px" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "14px" }} />
              )}
              {allExpanded ? "Recolher" : "Expandir"} Todas
            </Box>
          </Tooltip>
        </Box>

        {/* Lado direito: Filtros e seleção */}
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Contador de seleção */}
          {selectedLeads.size > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "rgba(0, 128, 105, 0.1)",
                padding: "3px 8px",
                borderRadius: "12px",
                border: "1px solid rgba(0, 128, 105, 0.2)",
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#008069",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "#008069",
                  fontWeight: 600,
                  fontSize: "10px",
                }}
              >
                {selectedLeads.size}
              </Typography>
              <Tooltip title="Limpar seleção" arrow>
                <IconButton
                  size="small"
                  onClick={onClearSelection}
                  sx={{
                    color: "#008069",
                    padding: "1px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 128, 105, 0.1)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: "12px" }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Filtro de leads copiados */}
          <Tooltip
            title={
              showCopiedLeads
                ? "Ocultar leads copiados"
                : "Mostrar leads copiados"
            }
            arrow
          >
            <Box
              component="button"
              onClick={onToggleCopiedLeads}
              sx={{
                backgroundColor: showCopiedLeads ? "#4caf50" : "transparent",
                color: showCopiedLeads ? "white" : "#4caf50",
                border: "1px solid #4caf50",
                borderRadius: "16px",
                minHeight: "26px",
                padding: "2px 10px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "11px",
                minWidth: "auto",
                boxShadow: showCopiedLeads
                  ? "0 1px 3px rgba(76, 175, 80, 0.2)"
                  : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                outline: "none",
                "&:focus-visible": {
                  outline: "2px solid #4caf50",
                  outlineOffset: "2px",
                  boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.2)",
                },
                "&:hover": {
                  backgroundColor: showCopiedLeads
                    ? "#45a049"
                    : "rgba(76, 175, 80, 0.1)",
                  borderColor: "#45a049",
                  transform: "translateY(-1px)",
                  boxShadow: showCopiedLeads
                    ? "0 2px 6px rgba(76, 175, 80, 0.3)"
                    : "0 1px 4px rgba(76, 175, 80, 0.15)",
                },
              }}
            >
              {showCopiedLeads ? (
                <VisibilityOffIcon sx={{ fontSize: "12px" }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: "12px" }} />
              )}
              {showCopiedLeads ? "Ocultar" : "Mostrar"}
            </Box>
          </Tooltip>

          {/* Contador de leads copiados */}
          {copiedLeadIds.size > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                padding: "2px 10px",
                minHeight: "26px",
                borderRadius: "16px",
                border: "1px solid rgba(76, 175, 80, 0.2)",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: "12px", color: "#4caf50" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#4caf50",
                  fontWeight: 700,
                  fontSize: "11px",
                  lineHeight: 1,
                }}
              >
                {copiedLeadIds.size}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LeadContactsControls;
